import React from "react"
import { Avatar, Button } from "@quorumus/plum"
import {
    CardContainer,
    LeftContentGroup,
    InfoStack,
    NameRow,
    NameText,
    RoleText,
    ActionsGroup,
    PartyBadge,
} from "QuorumGrassroots/widgets/ReusableComponents/OfficialCard/OfficialCard.styles"
import type {
    OfficialCardProps,
    OfficialAction,
    OfficialActionType,
} from "QuorumGrassroots/widgets/ReusableComponents/OfficialCard/OfficialCard.types"
import {
    XTwitterFarFAIcon,
    FacebookFarFAIcon,
    InstagramFarFAIcon,
    LinkedInFarFAIcon,
    YouTubeFarFAIcon,
    ExternalLinkFarFAIcon,
    ContactFarFAIcon,
} from "QuorumGrassroots/widgets/ReusableComponents/Icons"

const getPartyInitial = (party: string) => {
    if (!party) {
        return ""
    }

    const normalized = party.toLowerCase()

    if (normalized.startsWith("dem")) {
        return "D"
    }

    if (normalized.startsWith("rep")) {
        return "R"
    }

    return "I"
}

const handleDefaultActionNavigation = (action: OfficialAction) => {
    if (!action.url || typeof window === "undefined") {
        return
    }

    window.open(action.url, "_blank", "noopener,noreferrer")
}

const getActionIcon = (type: OfficialActionType): React.ComponentType<any> | null => {
    switch (type) {
        case "website":
            return ExternalLinkFarFAIcon
        case "contact":
            return ContactFarFAIcon
        case "twitter":
            return XTwitterFarFAIcon
        case "facebook":
            return FacebookFarFAIcon
        case "instagram":
            return InstagramFarFAIcon
        case "youtube":
            return YouTubeFarFAIcon
        case "linkedin":
            return LinkedInFarFAIcon
        default:
            return null
    }
}

export const OfficialCard: React.FC<OfficialCardProps> = ({
    official,
    actions,
    onClick,
    onActionClick,
    className,
    "data-plum-id": dataPlumId,
}) => {
    const { name, role, party, photoUrl } = official
    const basePlumId = dataPlumId ?? "official-card"

    const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
        if (!onClick) {
            return
        }

        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            onClick()
        }
    }

    const handleActionClick = (event: React.MouseEvent<HTMLButtonElement>, action: OfficialAction) => {
        event.stopPropagation()

        if (onActionClick) {
            onActionClick(action)
        } else {
            handleDefaultActionNavigation(action)
        }
    }

    const renderActions = () => {
        if (!actions || actions.length === 0) {
            return null
        }

        return (
            <ActionsGroup spacing={8} data-plum-id={`${basePlumId}.actions`} aria-label="Official actions">
                {actions.map((action) => {
                    const IconComponent = getActionIcon(action.type)
                    const isWebsite = action.type === "website"

                    return (
                        <Button
                            key={`${action.type}-${action.label}`}
                            variant={isWebsite ? "secondary" : "tertiary"}
                            size={isWebsite ? "lg" : "md"}
                            onClick={(event) => handleActionClick(event, action)}
                            aria-label={action.label}
                            rightSection={
                                isWebsite && IconComponent ? (
                                    <IconComponent size={20} color="currentColor" />
                                ) : undefined
                            }
                        >
                            {isWebsite ? action.label : undefined}
                            {!isWebsite && IconComponent ? <IconComponent size={18} color="currentColor" /> : undefined}
                        </Button>
                    )
                })}
            </ActionsGroup>
        )
    }

    return (
        <CardContainer
            onClick={onClick}
            isClickable={Boolean(onClick)}
            className={className}
            data-plum-id={basePlumId}
            role={onClick ? "button" : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={handleKeyDown}
        >
            <LeftContentGroup spacing={12} noWrap>
                <Avatar src={photoUrl} size="md" alt={name} data-plum-id={`${basePlumId}.avatar`} />
                <InfoStack>
                    <NameRow>
                        <NameText data-plum-id={`${basePlumId}.name`}>{name}</NameText>
                        <PartyBadge party={party} data-plum-id={`${basePlumId}.party`} aria-label={party}>
                            {getPartyInitial(party)}
                        </PartyBadge>
                    </NameRow>
                    <RoleText data-plum-id={`${basePlumId}.role`}>{role}</RoleText>
                </InfoStack>
            </LeftContentGroup>
            {renderActions()}
        </CardContainer>
    )
}
